import { AccordionSummary } from '@components/primitives/Accordion';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export const StyledAccordionSummary = styled(AccordionSummary)({
  alignItems: 'flex-start',
  '@container (min-width: 501px)': {
    alignItems: 'center',
  },
}) as typeof AccordionSummary;

/**
 * Container for AccordionSummary content with responsive grid layout
 */
export const SummaryContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  flexDirection: 'row',
  gap: theme.spacing(2),
  flexWrap: 'wrap',

  '@container (min-width: 501px) ': {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  '@container accordion (min-width: 901px)': {
    gridTemplateColumns: 'minmax(min-content, 190px) 1fr auto',
  },
})) as typeof Box;

/**
 * Avatar element for narrow/medium views (<701px)
 */
export const AvatarElement = styled(Box)(() => ({
  flexShrink: 0,
  display: 'block',
  '@container (min-width: 701px)': {
    display: 'none', // Hide in wide view
  },
})) as typeof Box;

/**
 * Time and name stacked vertically for narrow/medium views (<701px)
 */
export const TimeNameWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: '1 1 auto', // Allow to grow and shrink, but not break to new line
  minWidth: 0, // Important for text truncation to work
  maxWidth: 'calc(100% - 50px - 16px)', // Account for avatar and gap on narrow views
  '@container (min-width: 501px)': {
    flexGrow: 1,
    maxWidth: 'none',
  },
  '@container (min-width: 701px)': {
    display: 'none', // Hide in wide view
  },
})) as typeof Box;

/**
 * Avatar and time together for wide view (>=701px)
 */
export const AvatarTimeWrapper = styled(Box)(({ theme }) => ({
  display: 'none',
  '@container (min-width: 701px)': {
    gridColumn: '1',
    gridRow: '1',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
})) as typeof Box;

/**
 * Patient name alone for wide view (>=701px)
 */
export const PatientNameWrapper = styled(Box)(() => ({
  display: 'none',
  minWidth: 0, // Important for text truncation to work
  overflow: 'hidden', // Ensure text truncation works
  '@container (min-width: 701px)': {
    gridColumn: '2',
    gridRow: '1',
    display: 'block',
  },
})) as typeof Box;

/**
 * Action button wrapper
 */
export const ActionButtonWrapper = styled(Box)(() => ({
  flexShrink: 0,
  flexBasis: '100%', // Always take full width to force wrap to next line
  '@container (min-width: 501px)': {
    flexBasis: 'auto', // Normal sizing on wider viewports
  },
})) as typeof Box;

/**
 * Container for expanded content with responsive layout
 */
export const ExpandedContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),

  // Wider container: 3-column layout
  '@container accordion (min-width: 701px)': {
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: 'auto 1fr 1fr',
  },

  '@container accordion (min-width: 901px)': {
    gridTemplateColumns: 'minmax(min-content, 190px) 1fr 1fr',
  },
})) as typeof Box;

/**
 * Action links container with responsive positioning
 */
export const ActionLinksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  order: -1,

  '@container accordion (min-width: 701px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    alignSelf: 'start',
    paddingBottom: 0,
    borderBottom: 'none',
    order: 0,
  },
})) as typeof Box;

/**
 * Patient overview section
 */
export const PatientOverviewSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flex: 1,

  '@container accordion (min-width: 500px)': {
    minWidth: 0,
  },
})) as typeof Box;

/**
 * Last session summary section
 */
export const LastSessionSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flex: 1,

  '@container accordion (min-width: 500px)': {
    minWidth: 0,
  },
})) as typeof Box;

/**
 * Styled Typography for patient names with responsive truncation
 */
export const PatientNameText = styled(Typography)({
  fontWeight: 500,
  '@container (max-width: 500px)': {
    wordBreak: 'normal',
    overflowWrap: 'break-word',
  },
  '@container (min-width: 501px)': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}) as typeof Typography;
